'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import { Home, ChevronLeft, Filter, Users, Star, MapPin, Wifi, Wind, Info, Coffee, Utensils, Waves, SortDesc, Calendar } from 'lucide-react';
import Header from '../components/Header';

function AmenityIcon({ name }) {
    switch (name) {
        case 'wifi': return <Wifi className="h-4 w-4" />;
        case 'ac': return <Wind className="h-4 w-4" />;
        case 'parking': return <Info className="h-4 w-4" />;
        case 'pool': return <Waves className="h-4 w-4" />;
        case 'breakfast': return <Coffee className="h-4 w-4" />;
        case 'laundry': return <Utensils className="h-4 w-4" />;
        default: return null;
    }
}

function AvailabilityBadge({ availability }) {
    const colors = {
        available: 'bg-green-500 text-white shadow-lg shadow-green-500/30',
        fully_booked: 'bg-red-500 text-white shadow-lg shadow-red-500/30',
        under_maintenance: 'bg-amber-500 text-white shadow-lg shadow-amber-500/30',
    };
    return (
        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest ${colors[availability] || colors.available}`}>
            {availability.replace('_', ' ')}
        </span>
    );
}

export default function HotelsPage() {
    const router = useRouter();
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('rating');
    const [filters, setFilters] = useState({ wifi: false, ac: false, parking: false, pool: false, breakfast: false, laundry: false, type: 'all' });
    const [showFilters, setShowFilters] = useState(false);

    // Search State
    const [searchLocation, setSearchLocation] = useState('');
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');

    useEffect(() => {
        if (!router.isReady) return;
        
        // Load initial search from URL
        if (router.query.location) setSearchLocation(router.query.location);
        if (router.query.checkIn) setCheckIn(router.query.checkIn);
        if (router.query.checkOut) setCheckOut(router.query.checkOut);
    }, [router.isReady]);

    useEffect(() => {
        if (!router.isReady) return;
        const fetchHotels = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/hotels`);
                let data = await res.json();
                if (!Array.isArray(data)) data = [];

                // Filter by location
                if (searchLocation) {
                    data = data.filter(h => 
                        h.location.toLowerCase().includes(searchLocation.toLowerCase()) ||
                        h.name.toLowerCase().includes(searchLocation.toLowerCase())
                    );
                }

                // Filter by selected amenities
                const activeAmenities = Object.entries(filters).filter(([k, v]) => v && k !== 'type').map(([k]) => k);
                if (activeAmenities.length > 0) {
                    data = data.filter(h => activeAmenities.every(a => h.amenities && h.amenities[a]));
                }

                // Filter by type
                if (filters.type !== 'all') {
                    data = data.filter(h => h.type === filters.type);
                }

                // Sort
                if (sortBy === 'price-asc') data.sort((a, b) => (a.basePrice || 0) - (b.basePrice || 0));
                else if (sortBy === 'price-desc') data.sort((a, b) => (b.basePrice || 0) - (a.basePrice || 0));
                else if (sortBy === 'rating') data.sort((a, b) => b.rating - a.rating);

                setHotels(data);
            } catch (e) {
                console.error(e);
                setHotels([]);
            } finally {
                setLoading(false);
            }
        };
        fetchHotels();
    }, [router.isReady, sortBy, filters, searchLocation]);

    return (
        <div className="min-h-screen bg-gray-50">
            <Head><title>Stay With Us - EzyTravel Hotels</title></Head>

            <Header />

            <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 md:p-4 mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Where to?"
                                value={searchLocation}
                                onChange={e => setSearchLocation(e.target.value)}
                                className="pl-10 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500 outline-none"
                            />
                        </div>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="date"
                                value={checkIn}
                                onChange={e => setCheckIn(e.target.value)}
                                className="pl-10 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500 outline-none date-input-field"
                            />
                        </div>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="date"
                                value={checkOut}
                                onChange={e => setCheckOut(e.target.value)}
                                className="pl-10 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500 outline-none date-input-field"
                            />
                        </div>
                        <div className="flex gap-2">
                           <button className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 rounded-xl transition-all text-sm shadow-lg shadow-green-600/10">
                                Search
                           </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 flex items-center flex-wrap gap-3">
                                Hotels & Stays
                                <div className="flex bg-gray-200/50 p-1 rounded-xl text-[9px] font-black uppercase tracking-widest">
                                    <button 
                                        onClick={() => setFilters(f => ({ ...f, type: 'all' }))}
                                        className={`px-3 py-1.5 rounded-lg transition-all ${filters.type === 'all' ? 'bg-white shadow-sm text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                                    >All</button>
                                    <button 
                                        onClick={() => setFilters(f => ({ ...f, type: 'hotel' }))}
                                        className={`px-3 py-1.5 rounded-lg transition-all ${filters.type === 'hotel' ? 'bg-white shadow-sm text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                                    >Hotels</button>
                                    <button 
                                        onClick={() => setFilters(f => ({ ...f, type: 'homestay' }))}
                                        className={`px-3 py-1.5 rounded-lg transition-all ${filters.type === 'homestay' ? 'bg-white shadow-sm text-green-700' : 'text-gray-500 hover:text-gray-700'}`}
                                    >Homestays</button>
                                </div>
                            </h1>
                        </div>
                        <span className="text-gray-400 font-bold text-xs uppercase tracking-widest">{hotels.length} results found</span>
                    </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <div className="flex items-center gap-2 grow">
                        <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 flex items-center gap-2 grow sm:grow-0">
                            <SortDesc className="h-4 w-4 text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value)}
                                className="bg-transparent border-none text-xs font-bold text-gray-700 focus:ring-0 outline-none grow"
                            >
                                <option value="rating">Top Rated</option>
                                <option value="price-asc">Lowest Price</option>
                                <option value="price-desc">Highest Price</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center justify-center gap-2 border rounded-xl px-4 py-2 text-xs font-bold transition-all grow sm:grow-0 ${
                                showFilters ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 text-gray-700'
                            }`}
                        >
                            <Filter className="h-4 w-4" />
                            Filters {Object.values(filters).some(v => v === true) && '•'}
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                        <h3 className="font-semibold mb-4">Filter by Amenities</h3>
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(filters).filter(([k]) => k !== 'type').map(([key, val]) => (
                                <button
                                    key={key}
                                    onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${val ? 'bg-green-600 text-white border-green-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <AmenityIcon name={key} />
                                    <span className="capitalize">{key}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="flex justify-center items-center py-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    </div>
                )}

                {!loading && (
                    <div className="space-y-6">
                        {hotels.map(hotel => (
                            <div key={hotel._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col md:flex-row">
                                    <div className="md:w-1/3 h-56 md:h-auto grow-0 relative">
                                        <Link href={`/book/hotel/${hotel._id}`} className="block h-full group">
                                            <img
                                                src={hotel.imageUrl || `https://source.unsplash.com/800x400/?hotel,resort`}
                                                alt={hotel.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                                onError={e => { e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=400&fit=crop'; }}
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                        </Link>
                                        <div className="absolute top-3 left-3">
                                            <AvailabilityBadge availability={hotel.availability} />
                                        </div>
                                    </div>
                                
                                    <div className="p-5 md:p-6 lg:p-8 flex-1 flex flex-col">
                                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded shadow-sm ${
                                                        hotel.type === 'homestay' ? 'bg-amber-100 text-amber-700' : 'bg-green-50 text-green-600'
                                                    }`}>
                                                        {hotel.type === 'homestay' ? 'Homestay' : 'Hotel'}
                                                    </span>
                                                    <div className="flex items-center gap-0.5">
                                                        {[...Array(5)].map((_, i) => (
                                                            <Star key={i} className={`h-2.5 w-2.5 ${i < Math.floor(hotel.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />
                                                        ))}
                                                    </div>
                                                </div>
                                                <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 transition-colors">
                                                    {hotel.name}
                                                </h2>
                                                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mt-1">
                                                    <MapPin className="h-4 w-4 text-green-500" />
                                                    <span>{hotel.location}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center sm:flex-col sm:items-end justify-between w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                                                <div className="text-left sm:text-right">
                                                    <div className="text-2xl font-black text-gray-900 leading-none">₹{hotel.basePrice}</div>
                                                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">per night</div>
                                                </div>
                                                <div className="flex items-center gap-2 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100 sm:mt-2">
                                                    <div className="text-lg font-black text-green-700 leading-none">{hotel.rating}</div>
                                                    <div className="text-[9px] font-bold text-green-600 uppercase tracking-tight leading-tight">Excellent<br/>{hotel.reviews} reviews</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {hotel.amenities && Object.entries(hotel.amenities)
                                                .filter(([, v]) => v)
                                                .slice(0, 4)
                                                .map(([key]) => (
                                                    <div key={key} className="flex items-center gap-1.5 bg-gray-50 text-gray-500 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-lg border border-gray-100">
                                                        <AmenityIcon name={key} />
                                                        <span>{key}</span>
                                                    </div>
                                                ))}
                                        </div>

                                        <div className="mt-auto pt-6 flex flex-col sm:flex-row items-center gap-4 sm:justify-between border-t border-gray-50 mt-6">
                                            <p className="text-xs text-gray-500 leading-relaxed line-clamp-1 italic italic flex-1">
                                                {hotel.description}
                                            </p>
                                            
                                            {hotel.availability === 'fully_booked' ? (
                                                <button disabled className="w-full sm:w-auto bg-gray-100 text-gray-400 font-bold px-8 py-3 rounded-xl cursor-not-allowed text-xs transition-all uppercase tracking-widest">
                                                    Sold Out
                                                </button>
                                            ) : (
                                                <Link
                                                    href={`/book/hotel/${hotel._id}`}
                                                    className="w-full sm:w-auto text-center bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-3.5 rounded-xl transition-all text-xs shadow-lg shadow-green-600/20 uppercase tracking-widest"
                                                >
                                                    Book Now
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && hotels.length === 0 && (
                    <div className="text-center py-24">
                        <Home className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700">No hotels found</h3>
                        <p className="text-gray-500 mt-2">Try adjusting your filters or search a different location.</p>
                        <Link href="/" className="mt-6 inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors">
                            Back to Home
                        </Link>
                    </div>
                )}
            </main>
        </div>
    );
}
